import styles from './EmplyeesListStyles';
import {
  Body1,
  Button,
  Select,
  Tooltip,
  useFluent,
  useScrollbarWidth
} from '@fluentui/react-components';
import {
  Card,
  createTableColumn,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableRowData as RowStateBase,
  useTableFeatures,
  useTableSelection
} from '@fluentui/react-components/unstable';
import { FixedSizeList as List } from "react-window";
import React, { useContext, useEffect, useRef, useState } from 'react';
import UserContext from '../../modules/AppRouter/UserContext';
import { AppButton, AppErrorToast, AppLoader, appSuccessToast } from '../common';
import { userRoleEnum } from '../../utils/enums';
import { IExpectedHoursItem, IExpectedHoursTabProps } from '../../services/types/expectedHours';
import ExpectedHoursRow from '../businessComponenets/ExpectedHoursRow';
import { importExcelAndSend, fetchLastUpdated } from '../../services/excelImportService';
import { monthNames } from '../../utils/consts';

interface TableRowData extends RowStateBase<IExpectedHoursItem> {
  selected: boolean;
  appearance: "brand" | "none";
}

const VIRTUAL_ROW_HEIGHT = 48;

function renderHeaderWithTooltip(label: string, content: string | JSX.Element) {
  return (
    <span style={styles.headerTooltipLabel}>
      {label}
      <Tooltip relationship="description" content={content}>
        <span style={styles.headerTooltipIcon} aria-label={`Informacja: ${label}`}>i</span>
      </Tooltip>
    </span>
  );
}

const absentifyTooltipContent = (
  <div>
    Godziny nieobecności, które pomniejszają rozliczenie godzin usług.
    <br />
    Wliczane typy:
    <br />- wypoczynkowy urlop
    <br />- zwolnienie lekarskie
    <br />- niepełny etat
    <br />- macierzyński urlop
    <br />- ojcowski urlop
    <br />- okolicznościowy urlop
    <br />- bezpłatny urlop
    <br />- dzień wolny za święto
    <br />- przerwa w świadczeniu usług (B2B,UZ)
  </div>
);

const ignoredAbsentifyTooltipContent = (
  <div>
    Nieobecności nieuwzględniane w kolumnie Absentify:
    <br />- zdalna praca
    <br />- odbiór nadgodzin
  </div>
);

const totalTooltipContent = (
  <div>
    Rzeczywiście rozliczone godziny usług w miesiącu.
    <br />
    Liczenie:
    <br />
    YouTrack + nadgodziny 100% + (nadgodziny 150% x 1,5) + (nadgodziny 200% x 2) + odebrane nadgodziny/wyjścia.
  </div>
);

const targetTooltipContent = (
  <div>
    Docelowa liczba godzin możliwych do rozliczenia.
    <br />
    Bazowo:
    <br />
    Godziny robocze - Absentify + nadgodziny 100% + (nadgodziny 150% x 1,5) + (nadgodziny 200% x 2).
    <br />
    Dodatkowo, gdy Nadgodziny do odebrania &lt; 0, wynik jest pomniejszany o brakujące godziny.
  </div>
);

const columns = [
  createTableColumn<IExpectedHoursItem>({ columnId: "userName" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "contractType" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "youtrack" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "absentify" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "toCollect" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "collected" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "timePaidout100" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "timePaidout150" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "timePaidout200" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "absentifyIgnored" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "total" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "workingHours" }),
  createTableColumn<IExpectedHoursItem>({ columnId: "target" }),
];

const ExpectedHoursList: React.FC<IExpectedHoursTabProps> = ({
  tableHeadTxt,
  items,
  isLoading,
  getMoreItems,
  selectedMonth,
  selectedYear,
  setSelectedMonth,
  setSelectedYear,
  acceptSettlement,
  rejectSettlement
}) => {
  const { targetDocument } = useFluent();
  const scrollbarWidth = useScrollbarWidth({ targetDocument });

  const {
    getRows,
    selection: { toggleRow, isRowSelected },
  } = useTableFeatures(
    {
      columns,
      items,
    },
    [useTableSelection({ selectionMode: "multiselect" })]
  );

  const { userData } = useContext(UserContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [lastUpdated, setLastUpdated] = useState<string>('Aktualizacja Absentify: brak');
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const rows: TableRowData[] = getRows((row) => {
    const selected = isRowSelected(row.rowId);
    return {
      ...row,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === " ") {
          e.preventDefault();
          toggleRow(e, row.rowId);
        }
      },
      selected,
      appearance: selected ? "brand" : "none",
    };
  });

  const goBack = () => window.history.back();

  const onMonthSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMonth(e.target.value);
  };

  const onYearSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      await importExcelAndSend(file, selectedYear);

      appSuccessToast("✅ Dane zostały zaimportowane pomyślnie.");

      const updated = await fetchLastUpdated(selectedYear);
      setLastUpdated(`Aktualizacja Absentify: ${updated}`);

      getMoreItems();
    } catch (err: any) {
      AppErrorToast(err.message || "❌ Wystąpił błąd podczas importu pliku.");
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    fetchLastUpdated(selectedYear)
      .then((updated) => setLastUpdated(`Aktualizacja Absentify: ${updated}`))
      .catch(() => setLastUpdated('Aktualizacja Absentify: brak danych'));
  }, [selectedMonth, selectedYear]);

  return (
    <Card style={styles.tableContainer}>
      <Body1>
        <Button appearance="primary" onClick={goBack} style={styles.buttonBack}>
          Powrót
        </Button>

        <div style={styles.tableWithFiltersHeader}>
          <h3 style={styles.tableHeadTxt}>{tableHeadTxt}</h3>
          {userData.data.baseRole === userRoleEnum.Admin && (
            <div style={styles.tableFilterContainer}>
              Miesiąc:
              <Select style={styles.tableFilter} onChange={onMonthSelect} value={selectedMonth}>
                {monthNames.map((month) => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </Select>
              Rok:
              <Select style={styles.tableFilter} onChange={onYearSelect} value={selectedYear}>
                {Array.from({ length: 26 }, (_, i) => 2020 + i).map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Select>

              <AppButton
                text="Zaktualizuj dane z Absentify"
                disabled={isUploading}
                appearance="secondary"
                onClick={handleUploadClick}
              />
              <input
                type="file"
                accept=".xlsx"
                ref={fileInputRef}
                style={styles.hiddenFileInput}
                onChange={handleFileChange}
              />
              <div style={styles.lastUpdatedAbsentifyImportText}>{lastUpdated}</div>
            </div>
          )}
        </div>

        <div className="main-table-container">
          <Table
            noNativeElements
            aria-label="Tabela rozliczeń godzin usług"
            aria-rowcount={rows.length}
            style={styles.table}
          >
            <TableHeader>
              <TableRow aria-rowindex={1}>
                <TableHeaderCell>Nazwisko i imię</TableHeaderCell>
                <TableHeaderCell>Typ umowy</TableHeaderCell>
                <TableHeaderCell>YouTrack</TableHeaderCell>
                <TableHeaderCell>{renderHeaderWithTooltip("Absentify", absentifyTooltipContent)}</TableHeaderCell>
                <TableHeaderCell>Nadgodziny do odebrania</TableHeaderCell>
                <TableHeaderCell>Odebrane nadgodziny/wyjścia</TableHeaderCell>
                <TableHeaderCell>Nadgodziny 100%</TableHeaderCell>
                <TableHeaderCell>Nadgodziny 150%</TableHeaderCell>
                <TableHeaderCell>Nadgodziny 200%</TableHeaderCell>
                <TableHeaderCell>{renderHeaderWithTooltip("Zignorowane Absentify", ignoredAbsentifyTooltipContent)}</TableHeaderCell>
                <TableHeaderCell>{renderHeaderWithTooltip("Suma", totalTooltipContent)}</TableHeaderCell>
                <TableHeaderCell>Godziny robocze</TableHeaderCell>
                <TableHeaderCell>{renderHeaderWithTooltip("Docelowo", targetTooltipContent)}</TableHeaderCell>
                <TableHeaderCell style={styles.actionsHeaderCell}>Akcje</TableHeaderCell>
                <div role="presentation" style={{ width: scrollbarWidth }} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length <= 0 && !isLoading && (
                <h4 className="table-empty-value">Brak danych</h4>
              )}

              <List
                height={350}
                itemCount={rows.length}
                itemSize={VIRTUAL_ROW_HEIGHT}
                width="100%"
                itemData={rows}
              >
                {({ index, style, data }) => (
                  <ExpectedHoursRow
                    index={index}
                    style={style}
                    data={data}
                    acceptSettlement={acceptSettlement}
                    rejectSettlement={rejectSettlement}
                    canManageActions={userData.data.baseRole === userRoleEnum.Admin}
                  />
                )}
              </List>

              {isLoading && items.length === 0 && <AppLoader />}
            </TableBody>
          </Table>
        </div>
      </Body1>
    </Card>
  );
};

export default ExpectedHoursList;
